import { createApp, defineAsyncComponent } from "vue";
import App from "./App.vue";
import "./index.css";
import "@quasar/extras/material-icons/material-icons.css";
import "@quasar/extras/material-icons-outlined/material-icons-outlined.css";
import "@quasar/extras/fontawesome-v5/fontawesome-v5.css";
import iconSet from "quasar/icon-set/material-icons";
import "quasar/src/css/index.sass";
import { Quasar, Notify, Dialog, EventBus } from "quasar";
import HostnamePlugin from "./plugins/hostname";
import OpenLayersMap from "vue3-openlayers";
import "vue3-openlayers/dist/vue3-openlayers.css";

import cloudWatchRum from "./cloudWatchRum";

import Highcharts from "highcharts";
import TimelineModule from "highcharts/modules/timeline";
TimelineModule(Highcharts);

import store from "./store";
import router from "./router/index";

import Button from "./components/reusable/Button.vue";
import i18n, { loadLocaleMessages } from "./i18n";
import DomainHandler from "./whitelabel/domainHandler";

// Detect chunk load failures after a deploy and prompt the user to reload
// https://vite.dev/guide/build#load-error-handling
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault(); // prevent the error from throwing
    if (window.confirm("A new version of Compass is available. Reload now?")) {
      window.location.reload();
    }
  });
}

function checkCookies() {
  if (!navigator.cookieEnabled) {
    window.location.href = "/cookies.html";
  }
}
checkCookies();

const bus = new EventBus();
bus.on("logout", () => {
  router.push("/login");
});

async function adjustThemeVariables() {
  const defaultTheme = import("./css/themes/default/variables.js");
  const hostname = DomainHandler.getHostname();

  try {
    let themeModule;
    try {
      themeModule = await import(`./whitelabel/tenants/${hostname}/css/themes/variables.js`);
    } catch (error) {
      themeModule = await defaultTheme;
    }

    const theme = themeModule.default;
    Object.entries(theme).forEach(([key, val]) => {
      document.documentElement.style.setProperty(key, val);
    });
  } catch (error) {
    // Silently fall back to default theme
  }
}

async function loadTheme() {
  const hostName = DomainHandler.getHostname();
  await import(`./whitelabel/tenants/${hostName}/css/themes/index.sass`).catch(() => {
    import("./css/themes/default/index.sass");
  });
}

async function loadApp() {
  // Initialize white label variables globally
  const { whiteLabelRegistry } = await import("./whitelabel");
  await whiteLabelRegistry.initVariables();
  await adjustThemeVariables();
  await loadTheme();
  await Promise.all([loadLocaleMessages(i18n.global.locale.value), loadLocaleMessages("en-US")]);

  const app = createApp(App)
    .use(router)
    .use(store)
    .use(OpenLayersMap)
    .use(Quasar, {
      iconSet,
      plugins: {
        Notify,
        Dialog,
      },
      config: {
        brand: {
          primary: "#00a6b5",
          accent: "#FFEB03",
        },
      },
    })
    .use(i18n)
    .component("MainButton", Button)
    .component(
      "MainInput",
      defineAsyncComponent(() => import("./components/reusable/Input.vue"))
    )
    .component(
      "MainDatepicker",
      defineAsyncComponent(() => import("./components/reusable/Datepicker.vue"))
    )
    .component(
      "MainToggleButton",
      defineAsyncComponent(() => import("./components/reusable/ToggleButton.vue"))
    )
    .use(cloudWatchRum)
    .use(HostnamePlugin);

  app.config.errorHandler = (err, _instance, info) => {
    console.error(err);
    const cloudWatchRum = app.config.globalProperties.cloudWatchRum;
    // Pass the Error object (not a string) so RUM captures message + stack trace.
    const error = err instanceof Error ? err : new Error(String(err));
    cloudWatchRum("recordError", error);
    // Vue lifecycle context (e.g. "render function", "watcher") would be lost by
    // recordError, so attach it as a companion event for triage.
    if (info) {
      cloudWatchRum("recordEvent", { type: "vue_error_info", data: { info, message: error.message } });
    }
  };

  app.mount("#app");
}

loadApp();
